<template>
  <Teleport to="body">
    <Transition name="tour-slide">
      <div
        v-if="open"
        class="tour-panel"
        role="dialog"
        aria-label="Product tour"
      >
        <!-- Header -->
        <div class="tour-head">
          <div class="tour-head__brand">
            <span class="tour-step-count">{{ step + 1 }} / {{ total }}</span>
            <span class="tour-head__title">Getting started</span>
          </div>
          <button
            class="tour-close"
            @click="(endTour(), $emit('update:open', false))"
            title="Close tour"
          >
            <v-icon size="15">mdi-close</v-icon>
          </button>
        </div>

        <!-- Progress bar -->
        <div class="tour-progress">
          <div
            class="tour-progress__fill"
            :style="{ width: progress + '%' }"
          />
        </div>

        <!-- Content -->
        <Transition
          :name="direction === 'forward' ? 'tour-next' : 'tour-prev'"
          mode="out-in"
        >
          <div
            :key="currentStep.id"
            class="tour-body"
          >
            <div class="tour-icon">{{ currentStep.icon }}</div>
            <h3 class="tour-title">{{ currentStep.title }}</h3>
            <p class="tour-desc">{{ currentStep.body }}</p>
            <span
              v-if="currentStep.plan"
              class="tour-plan-badge"
              >{{ currentStep.plan }}</span
            >
          </div>
        </Transition>

        <!-- Footer -->
        <div class="tour-footer">
          <button
            v-if="step > 0"
            class="tour-btn tour-btn--ghost"
            @click="prev"
          >
            ← Back
          </button>
          <span
            v-else
            class="tour-skip"
            @click="(endTour(), $emit('update:open', false))"
            >Skip tour</span
          >

          <div class="tour-footer__right">
            <NuxtLink
              v-if="currentStep.href"
              :to="currentStep.href"
              class="tour-goto"
              @click.passive="() => {}"
            >
              {{ currentStep.hrefLabel ?? "Go there" }}
            </NuxtLink>
            <button
              class="tour-btn tour-btn--primary"
              @click="next"
            >
              {{ isLastStep ? "Finish" : "Next →" }}
            </button>
          </div>
        </div>

        <!-- Dot indicators -->
        <div class="tour-dots">
          <button
            v-for="(s, i) in total"
            :key="i"
            class="tour-dot"
            :class="{ active: i === step }"
            @click="jumpTo(i)"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { TOUR_STEPS, useTour } from "@/composables/useTour";

interface Props {
  open: boolean;
}
interface Emits {
  (e: "update:open", value: boolean): void;
}
const { step, currentStep, isLastStep, progress, nextStep, prevStep, endTour } =
  useTour();

defineProps<Props>();
defineEmits<Emits>();

const total = TOUR_STEPS.length;
const direction = ref<"forward" | "back">("forward");

function next() {
  direction.value = "forward";
  nextStep();
}

function prev() {
  direction.value = "back";
  prevStep();
}

function jumpTo(i: number) {
  direction.value = i > step.value ? "forward" : "back";
  step.value = i;
}
</script>

<style scoped lang="scss">
$primary: var(--app-primary-500);
$radius: 18px;

.tour-panel {
  position: fixed;
  bottom: 28px;
  right: 28px;
  width: 340px;
  background: var(--app-color-surface);
  border-radius: $radius;
  box-shadow:
    0 4px 6px var(--app-slate-900-alpha-4),
    0 20px 60px var(--app-slate-900-alpha-16);
  z-index: 9999;
  overflow: hidden;
  border: 1px solid var(--app-slate-200);
}

/* Header */
.tour-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 1px solid var(--app-slate-100);
}

.tour-head__brand {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tour-head__title {
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--app-slate-400);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.tour-step-count {
  font-size: 0.72rem;
  font-weight: 700;
  color: $primary;
  background: var(--app-primary-500-alpha-10);
  padding: 2px 8px;
  border-radius: 999px;
}

.tour-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  color: var(--app-slate-400);
  transition: background 0.12s;

  &:hover {
    background: var(--app-slate-100);
    color: var(--app-slate-600);
  }
}

/* Progress bar */
.tour-progress {
  height: 3px;
  background: var(--app-slate-100);
}

.tour-progress__fill {
  height: 100%;
  background: $primary;
  transition: width 0.3s ease;
}

/* Body */
.tour-body {
  padding: 20px 20px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 140px;
}

.tour-icon {
  font-size: 2rem;
  line-height: 1;
}

.tour-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--app-color-foreground);
  line-height: 1.3;
}

.tour-desc {
  font-size: 0.84rem;
  color: var(--app-slate-600);
  line-height: 1.6;
}

.tour-plan-badge {
  display: inline-flex;
  align-self: flex-start;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--app-violet-500-alpha-10);
  color: var(--app-violet-600);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.06em;
}

/* Footer */
.tour-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px 14px;
  gap: 8px;
}

.tour-footer__right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tour-skip {
  font-size: 0.78rem;
  color: var(--app-slate-400);
  cursor: pointer;
  font-weight: 500;

  &:hover {
    color: var(--app-slate-500);
  }
}

.tour-goto {
  font-size: 0.78rem;
  color: $primary;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.tour-btn {
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  border-radius: 9px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: all 0.12s;

  &--primary {
    background: $primary;
    color: var(--app-white);

    &:hover {
      filter: brightness(1.07);
    }
  }

  &--ghost {
    background: var(--app-slate-50);
    color: var(--app-slate-600);
    border: 1px solid var(--app-slate-200);

    &:hover {
      border-color: $primary;
      color: $primary;
    }
  }
}

/* Dot indicators */
.tour-dots {
  display: flex;
  justify-content: center;
  gap: 5px;
  padding-bottom: 14px;
}

.tour-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--app-slate-200);
  border: none;
  cursor: pointer;
  transition:
    background 0.2s,
    width 0.2s;
  padding: 0;

  &.active {
    background: $primary;
    width: 18px;
    border-radius: 3px;
  }
}

/* Panel slide-in */
.tour-slide-enter-active,
.tour-slide-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}
.tour-slide-enter-from,
.tour-slide-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

/* Step transitions */
.tour-next-enter-active,
.tour-next-leave-active,
.tour-prev-enter-active,
.tour-prev-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}
.tour-next-enter-from {
  opacity: 0;
  transform: translateX(16px);
}
.tour-next-leave-to {
  opacity: 0;
  transform: translateX(-16px);
}
.tour-prev-enter-from {
  opacity: 0;
  transform: translateX(-16px);
}
.tour-prev-leave-to {
  opacity: 0;
  transform: translateX(16px);
}
</style>
